import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type PrizeType = 'good_luck_next_time' | 'free_spin' | 'usdt5' | 'usdt100' | 'iphone17'

export interface SpinResult {
  id: string
  user_id: string
  prize_type: PrizeType
  points_spent: number
  points_before: number
  points_after: number
  segment_index: number
  seed: number
  created_at: string
}

export interface User {
  id: string
  email: string
  balance: number
}

interface AppState {
  user: User | null
  spins: SpinResult[]
  theme: 'light' | 'dark'
  soundEnabled: boolean
  isSpinning: boolean
  lastSpinResult: SpinResult | null
  lastActivity: number

  setUser: (user: User) => void
  logout: () => void
  setBalance: (balance: number) => void
  addSpin: (spin: SpinResult) => void
  setTheme: (theme: 'light' | 'dark') => void
  toggleTheme: () => void
  setSoundEnabled: (enabled: boolean) => void
  toggleSound: () => void
  setSpinning: (spinning: boolean) => void
  setLastSpinResult: (result: SpinResult | null) => void
  touch: () => void
}

function detectSystemTheme(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark'
  }
  return 'light'
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      user: null,
      spins: [],
      theme: detectSystemTheme(),
      soundEnabled: true,
      isSpinning: false,
      lastSpinResult: null,
      lastActivity: Date.now(),

      setUser: (user) => set({ user, lastActivity: Date.now() }),
      logout: () => set({ user: null, spins: [], lastSpinResult: null }),
      setBalance: (balance) => set((state) => ({
        user: state.user ? { ...state.user, balance } : null
      })),
      addSpin: (spin) => set((state) => ({
        spins: [spin, ...state.spins].slice(0, 50),
        user: state.user ? { ...state.user, balance: spin.points_after } : null,
        lastActivity: Date.now(),
      })),
      setTheme: (theme) => {
        if (theme === 'dark') {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
        set({ theme })
      },
      toggleTheme: () => {
        const next = get().theme === 'dark' ? 'light' : 'dark'
        if (next === 'dark') {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
        set({ theme: next })
      },
      setSoundEnabled: (enabled) => set({ soundEnabled: enabled }),
      toggleSound: () => set((s) => ({ soundEnabled: !s.soundEnabled })),
      setSpinning: (isSpinning) => set({ isSpinning }),
      setLastSpinResult: (result) => set({ lastSpinResult: result }),
      touch: () => set({ lastActivity: Date.now() }),
    }),
    {
      name: 'perceptrons-prize-wheel',
      version: 4,
      migrate: (persisted: unknown, version: number) => {
        const state = persisted as Record<string, unknown>
        if (version < 4) {
          return { ...state, user: null, spins: [], soundEnabled: true, theme: 'light' }
        }
        return state
      },
      partialize: (state) => ({
        theme: state.theme,
        soundEnabled: state.soundEnabled,
        user: state.user,
        spins: state.spins,
      }),
    }
  )
)

// Apply theme on load
if (typeof window !== 'undefined') {
  const stored = localStorage.getItem('perceptrons-prize-wheel')
  if (stored) {
    try {
      const parsed = JSON.parse(stored)
      if (parsed?.state?.theme === 'dark') {
        document.documentElement.classList.add('dark')
      }
    } catch { /* ignore */ }
  }
}
